import { useId, useState } from 'react';

import { useT } from '../../i18n';
import type { LocaleContextValue } from '../../i18n';
import { safeId } from './labelledControl';

export interface EloWeightSliderProps {
  /** Current ELO weight as a 0–2 fraction (0 = 0 %, 1 = 100 %, 2 = 200 %). */
  value: number;
  /** Called with the next fraction (percent / 100). */
  onChange: (value: number) => void;
  /** Highlighted "recommended" percent on the scale (default 100). */
  recommended?: number;
  /**
   * Skips the lock entirely (e.g. single-match registration) — there the ELO
   * weight is a deliberate per-match choice, not a protected stored setting.
   */
  alwaysUnlocked?: boolean;
}

// ─────────────────────────────────────────────────────────────────────
// ELO-weight display helpers — co-located with EloWeightSlider, the only
// consumer. Kept byte-identical to the original artifact (legacy/App.jsx)
// so the contextual descriptions stay identical across every ELO adjustment
// mechanism (single match, KM, americano …).
// ─────────────────────────────────────────────────────────────────────
const ELO_WEIGHT_LEVELS = [
  { value: 0, shortKey: 'ui.eloWeight.level0.short', descKey: 'ui.eloWeight.level0.desc' },
  { value: 50, shortKey: 'ui.eloWeight.level50.short', descKey: 'ui.eloWeight.level50.desc' },
  { value: 100, shortKey: 'ui.eloWeight.level100.short', descKey: 'ui.eloWeight.level100.desc' },
  { value: 150, shortKey: 'ui.eloWeight.level150.short', descKey: 'ui.eloWeight.level150.desc' },
  { value: 200, shortKey: 'ui.eloWeight.level200.short', descKey: 'ui.eloWeight.level200.desc' },
] as const;

// Returns { value, short, desc } for a given ELO weight in percent (0-200).
// Exact levels are looked up directly; in-between values are described relative to their neighbours.
// The translator is PASSED IN: this runs outside the component, where a hook
// cannot. (No backticks in this comment — the raw-string guard pairs them
// across the whole file, and an unmatched pair swallows the code after it.)
function eloWeightInfo(percent, t: LocaleContextValue['t']) {
  const p = Math.round(Number(percent) || 0);
  const exact = ELO_WEIGHT_LEVELS.find((l) => l.value === p);
  if (exact) return { value: exact.value, short: t(exact.shortKey), desc: t(exact.descKey) };
  const below = [...ELO_WEIGHT_LEVELS].reverse().find((l) => l.value < p);
  const above = ELO_WEIGHT_LEVELS.find((l) => l.value > p);
  if (below && above) {
    const belowShort = t(below.shortKey);
    const aboveShort = t(above.shortKey);
    return {
      value: p,
      short: t('ui.eloWeight.mixShort', {
        below: belowShort.toLowerCase(),
        above: aboveShort.toLowerCase(),
      }),
      desc: t('ui.eloWeight.mixDesc', {
        below: belowShort,
        above: aboveShort,
        belowPct: String(below.value),
        abovePct: String(above.value),
      }),
    };
  }
  const edge = below || above;
  return {
    value: p,
    short: edge ? t(edge.shortKey) : `${p} %`,
    desc: edge ? t(edge.descKey) : t('ui.eloWeight.customDesc', { percent: String(p) }),
  };
}

/**
 * ELO-weight slider (locked by default; range 0–200 % in 25 % steps);
 * extracted verbatim from the original artifact (legacy/App.jsx). Markup is
 * unchanged so styling is identical wherever it renders.
 */
export function EloWeightSlider({
  value,
  onChange,
  recommended = 100,
  alwaysUnlocked = false,
}: EloWeightSliderProps) {
  const t = useT();
  const sliderId = safeId('elo-weight', useId());
  // The slider is locked by default — the user must actively click the unlock
  // toggle to adjust the ELO weight. Prevents accidental changes from mishaps or
  // exploration, since the ELO weight directly affects players' ratings.
  // alwaysUnlocked=true (e.g. single-match registration) skips the lock entirely:
  // there the ELO weight is a deliberate PER-match choice, not a protected stored setting.
  const [unlocked, setUnlocked] = useState(false);
  const isUnlocked = alwaysUnlocked || unlocked;

  const percent = Math.round(value * 100);

  const handleChange = (e) => {
    const newPercent = parseInt(e.target.value, 10);
    onChange(newPercent / 100);
  };

  // Contextual explanation — uses the SHARED descriptions (eloWeightInfo) so that
  // the text is identical across every ELO-adjustment mechanism (single match, KM, americano …).
  const info = eloWeightInfo(percent, t);
  const label = info.desc;

  // Colour based on the value
  const color = !isUnlocked
    ? '#94a098' // grey when locked — visual signal for disabled
    : percent === 0
      ? '#94a098'
      : percent < 100
        ? '#3b82f6' // blue
        : percent === 100
          ? '#65a30d' // lime-deep
          : '#dc2626'; // red when over 100

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2">
        <label
          htmlFor={sliderId}
          className="font-mono text-[10px] uppercase tracking-wider text-ink-3"
        >
          {t('ui.eloWeight.label')}
        </label>
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-xs font-bold px-2.5 py-1 rounded-full transition-colors"
            style={{ background: color + '22', color }}
          >
            {percent}%
          </span>
          {/* Toggle between locked and unlocked state — hidden when alwaysUnlocked
              (single matches have nothing to protect; weight is chosen freely per match). */}
          {!alwaysUnlocked && (
            <button
              type="button"
              onClick={() => setUnlocked((u) => !u)}
              className="text-[11px] font-mono px-2.5 py-1 rounded-full border transition-colors"
              style={
                unlocked
                  ? { background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }
                  : { background: 'white', color: '#3a4a40', borderColor: '#d4dad6' }
              }
              title={unlocked ? t('ui.eloWeight.lockHint') : t('ui.eloWeight.unlockHint')}
            >
              {unlocked ? t('ui.eloWeight.unlocked') : t('ui.eloWeight.edit')}
            </button>
          )}
        </div>
      </div>

      {/* Slider with 25% steps, range 0-200. Disabled style when locked. */}
      <div
        className="relative pt-2 pb-6 transition-opacity"
        style={{ opacity: isUnlocked ? 1 : 0.4, pointerEvents: isUnlocked ? 'auto' : 'none' }}
      >
        {/* Tick marks behind the slider — show 50, 100, 150 as recommended */}
        <div className="absolute inset-x-0 top-2 h-2 pointer-events-none flex items-center">
          {[50, 100, 150].map((tick) => (
            <div
              key={tick}
              className="absolute w-px h-3 -translate-y-0.5"
              style={{
                left: `${(tick / 200) * 100}%`,
                background: tick === 100 ? '#65a30d' : '#94a098',
              }}
            />
          ))}
        </div>

        <input
          id={sliderId}
          type="range"
          min="0"
          max="200"
          step="25"
          value={percent}
          onChange={handleChange}
          disabled={!isUnlocked}
          className="w-full cursor-pointer relative z-10 disabled:cursor-not-allowed"
          style={{ accentColor: color }}
        />

        {/* Number labels below. A click on one jumps the slider there; that only
            repeats what the slider's own arrow keys do, so the labels are
            role="presentation", not controls (#900). */}
        <div className="absolute inset-x-0 bottom-0 flex justify-between text-[9px] font-mono text-ink-3 px-0.5">
          {[0, 50, 100, 150, 200].map((n) => (
            <span
              key={n}
              role="presentation"
              className={isUnlocked ? 'cursor-pointer hover:text-ink-1' : ''}
              onClick={() => isUnlocked && onChange(n / 100)}
              style={{
                fontWeight: percent === n ? 700 : 400,
                color: n === recommended ? '#65a30d' : undefined,
              }}
            >
              {n}
            </span>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-ink-3 leading-relaxed">
        {label}
        {isUnlocked && recommended && percent !== recommended && (
          <button
            onClick={() => onChange(recommended / 100)}
            className="ml-2 text-lime-deep underline-offset-2 hover:underline"
          >
            {t('ui.eloWeight.recommended', { percent: String(recommended) })}
          </button>
        )}
      </p>
    </div>
  );
}
